import { useState, useEffect } from "react";
import PageHeading from "../PageHeading";
import ExportOptions from "../ExportOptions";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/wastage";
const API_KEY =
  process.env.REACT_APP_API_KEY ||
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const money = (n) => (Number(n) || 0).toFixed(2);

// Store expense = wastage loss only (asset purchase is an investment, not expense).
const StoreAccounts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const limit = 10;

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Store" },
  ];

  const headers = ["Sl", "Asset Name", "Stock (Qty)", "Wastage Qty", "Total Loss"];

  const dataMapper = (r, index) =>
    [index + 1, r.assetName, r.quantity ?? 0, r.wastage ?? 0, money(r.totalLoss)].join("\t");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(API_BASE, {
          method: "GET",
          headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
        });
        const data = await res.json();
        const list = (data.assets || []).map((a) => ({
          id: a.id,
          assetName: a.assetName,
          quantity: a.quantity ?? 0,
          wastage: a.wastage ?? 0,
          totalLoss: Number(a.totalLoss) || 0,
        }));
        setRows(list);
        setCurrentPage(1);
      } catch (err) {
        setError("Unable to connect to the server. Please try again later.");
        console.error("Store accounts fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = rows.filter((r) =>
    r.assetName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalLoss = filtered.reduce((sum, r) => sum + r.totalLoss, 0);
  const totalWastageQty = filtered.reduce((sum, r) => sum + (Number(r.wastage) || 0), 0);

  const totalPages = Math.ceil(filtered.length / limit) || 1;
  const pagedRows = filtered.slice((currentPage - 1) * limit, currentPage * limit);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  return (
    <div>
      <PageHeading title="Store" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header + search */}
        <div className="border-b border-dashed border-gray-300 p-4 flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-base font-medium text-gray-700">Store Accounts (Wastage Loss)</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
              placeholder="Search by asset name"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            <ExportOptions
              data={filtered}
              title="Store Accounts Report"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen((p) => !p)}
            />
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Loading report...</p>
          ) : error ? (
            <p className="text-center text-red-500 py-10">{error}</p>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-slate-400" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Assets</p>
                  <p className="text-2xl font-bold text-slate-700 mt-2">{filtered.length}</p>
                  <p className="text-xs text-gray-400 mt-1">Asset types</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-amber-500" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Wastage Qty</p>
                  <p className="text-2xl font-bold text-amber-600 mt-2">{totalWastageQty}</p>
                  <p className="text-xs text-gray-400 mt-1">Units wasted</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-rose-500" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Total Loss (Expense)</p>
                  <p className="text-2xl font-bold text-rose-600 mt-2">৳ {money(totalLoss)}</p>
                  <p className="text-xs text-gray-400 mt-1">Wastage loss</p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-[700px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-[#FBFBFD]">
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Sl</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Asset Name</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Stock (Qty)</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Wastage Qty</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-red-700 p-3">Total Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length > 0 ? (
                      pagedRows.map((r, index) => (
                        <tr key={r.id} className="hover:bg-gray-50 border-b">
                          <td className="text-left font-inter text-sm text-M-heading-color p-3">{(currentPage - 1) * limit + index + 1}</td>
                          <td className="text-left capitalize font-inter text-sm text-M-heading-color p-3">{r.assetName}</td>
                          <td className="text-right font-inter text-sm text-M-heading-color p-3">{r.quantity}</td>
                          <td className="text-right font-inter text-sm text-amber-600 p-3">{r.wastage}</td>
                          <td className="text-right font-inter text-sm font-semibold text-red-600 p-3">৳ {money(r.totalLoss)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="5" className="text-center p-4 text-gray-500">No store data found</td></tr>
                    )}

                    {filtered.length > 0 && (
                      <tr className="border-t-2 border-M-text-color/30 bg-gray-50 font-bold">
                        <td className="text-right font-inter text-sm text-M-heading-color p-3" colSpan={3}>Total:</td>
                        <td className="text-right font-inter text-sm text-amber-700 p-3">{totalWastageQty}</td>
                        <td className="text-right font-inter text-sm text-red-700 p-3">৳ {money(totalLoss)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={filtered.length}
                itemsPerPage={limit}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default StoreAccounts;